/**
 * Country list for the profile country selector and the country leaderboard
 * filter.
 *
 * Strategy (revised May 17, 2026, cross-checked against the FIFA-published
 * 48-team field for the 2026 World Cup):
 *   - Curvle supports EVERY country in the world. The full ISO 3166-1
 *     alpha-2 set (~249 entries) is selectable.
 *   - `featured: true` marks the "Featured" rail in the picker. The rule:
 *
 *         featured = FIFA 2026 World Cup qualifier
 *
 *     Strictly the 48 World Cup teams — no engagement-based extras. India,
 *     Indonesia, China, Russia, Vietnam were previously featured for
 *     football-audience reasons and have been demoted; same for the
 *     2022-but-not-2026 qualifiers (Italy, Cameroon, Denmark, Poland,
 *     Serbia, Bolivia, Chile, Peru, Nigeria, Ukraine, UAE, Venezuela).
 *
 *     COUNT NOTE: 47 featured entries cover the 48 teams. England and
 *     Scotland are two distinct World Cup teams but share the same
 *     ISO 3166-1 code (GB / United Kingdom). We keep GB as a single
 *     featured entry — a player picking GB is implicitly cheering for
 *     either of the two UK teams.
 *
 *   - Everyone else lives in the alphabetical "All countries" list.
 *   - Nobody is in an "Others" bucket — that is design malpractice.
 *
 * "XX" is a sentinel meaning "unset / Worldwide". The server accepts any
 * ISO 3166-1 alpha-2 code.
 *
 * Flag rendering — TWO helpers, pick the right one:
 *
 *   flagUrl(cc, w)   PNG flag served via flagcdn.com CDN. PREFERRED for
 *                    cross-platform rendering — Windows + most desktop
 *                    Linux distros do NOT render regional-indicator emoji
 *                    as flags (they show the two-letter code in boxes
 *                    instead). Use as `<img src={flagUrl(c.code)}>` with
 *                    loading="lazy" and fixed width/height.
 *
 *   flag(cc)         Regional-indicator-pair emoji string. Renders as a
 *                    flag on iOS/Android/macOS, but NOT on Windows. Keep
 *                    using only in text-only contexts (alt text, server
 *                    fallbacks) where an <img> isn't available. Returns
 *                    🌐 for "XX" / unknown / non-2-letter codes.
 */
export interface Country {
  /** ISO 3166-1 alpha-2 code, uppercase. */
  code: string;
  /** Display name in English. */
  name: string;
  /**
   * Featured tier — surfaced in the "Featured" rail of the picker. Set for
   * the FIFA 2026 World Cup qualifiers ONLY. Non-qualifier engagement
   * entries are NOT featured (per May 17 launch polish decision).
   */
  featured: boolean;
}

/** "XX" means the user hasn't picked a country yet. The selector shows it
 *  as "Worldwide / unset" so the user understands what's happening. */
export const UNSET_COUNTRY = "XX" as const;

/**
 * The full ISO 3166-1 alpha-2 set, alphabetised by display name.
 * `featured: true` marks Curvle's "Featured" rail — strictly the FIFA 2026
 * World Cup qualifiers. Adding/removing a country here is the single
 * source of truth — no server deploy needed.
 */
export const COUNTRIES: Country[] = [
  { code: "AF", name: "Afghanistan", featured: false },
  { code: "AX", name: "Åland Islands", featured: false },
  { code: "AL", name: "Albania", featured: false },
  { code: "DZ", name: "Algeria", featured: true },
  { code: "AS", name: "American Samoa", featured: false },
  { code: "AD", name: "Andorra", featured: false },
  { code: "AO", name: "Angola", featured: false },
  { code: "AI", name: "Anguilla", featured: false },
  { code: "AQ", name: "Antarctica", featured: false },
  { code: "AG", name: "Antigua and Barbuda", featured: false },
  { code: "AR", name: "Argentina", featured: true },
  { code: "AM", name: "Armenia", featured: false },
  { code: "AW", name: "Aruba", featured: false },
  { code: "AU", name: "Australia", featured: true },
  { code: "AT", name: "Austria", featured: true },
  { code: "AZ", name: "Azerbaijan", featured: false },
  { code: "BS", name: "Bahamas", featured: false },
  { code: "BH", name: "Bahrain", featured: false },
  { code: "BD", name: "Bangladesh", featured: false },
  { code: "BB", name: "Barbados", featured: false },
  { code: "BY", name: "Belarus", featured: false },
  { code: "BE", name: "Belgium", featured: true },
  { code: "BZ", name: "Belize", featured: false },
  { code: "BJ", name: "Benin", featured: false },
  { code: "BM", name: "Bermuda", featured: false },
  { code: "BT", name: "Bhutan", featured: false },
  { code: "BO", name: "Bolivia", featured: false },
  { code: "BA", name: "Bosnia and Herzegovina", featured: true },
  { code: "BW", name: "Botswana", featured: false },
  { code: "BV", name: "Bouvet Island", featured: false },
  { code: "BR", name: "Brazil", featured: true },
  { code: "IO", name: "British Indian Ocean Territory", featured: false },
  { code: "BN", name: "Brunei", featured: false },
  { code: "BG", name: "Bulgaria", featured: false },
  { code: "BF", name: "Burkina Faso", featured: false },
  { code: "BI", name: "Burundi", featured: false },
  { code: "CV", name: "Cabo Verde", featured: true },
  { code: "KH", name: "Cambodia", featured: false },
  { code: "CM", name: "Cameroon", featured: false },
  { code: "CA", name: "Canada", featured: true },
  { code: "KY", name: "Cayman Islands", featured: false },
  { code: "CF", name: "Central African Republic", featured: false },
  { code: "TD", name: "Chad", featured: false },
  { code: "CL", name: "Chile", featured: false },
  { code: "CN", name: "China", featured: false },
  { code: "CX", name: "Christmas Island", featured: false },
  { code: "CC", name: "Cocos (Keeling) Islands", featured: false },
  { code: "CO", name: "Colombia", featured: true },
  { code: "KM", name: "Comoros", featured: false },
  { code: "CG", name: "Congo (Republic)", featured: false },
  { code: "CD", name: "Congo (DRC)", featured: true },
  { code: "CK", name: "Cook Islands", featured: false },
  { code: "CR", name: "Costa Rica", featured: false },
  { code: "CI", name: "Côte d'Ivoire", featured: true },
  { code: "HR", name: "Croatia", featured: true },
  { code: "CU", name: "Cuba", featured: false },
  { code: "CW", name: "Curaçao", featured: true },
  { code: "CY", name: "Cyprus", featured: false },
  { code: "CZ", name: "Czechia", featured: true },
  { code: "DK", name: "Denmark", featured: false },
  { code: "DJ", name: "Djibouti", featured: false },
  { code: "DM", name: "Dominica", featured: false },
  { code: "DO", name: "Dominican Republic", featured: false },
  { code: "EC", name: "Ecuador", featured: true },
  { code: "EG", name: "Egypt", featured: true },
  { code: "SV", name: "El Salvador", featured: false },
  { code: "GQ", name: "Equatorial Guinea", featured: false },
  { code: "ER", name: "Eritrea", featured: false },
  { code: "EE", name: "Estonia", featured: false },
  { code: "SZ", name: "Eswatini", featured: false },
  { code: "ET", name: "Ethiopia", featured: false },
  { code: "FK", name: "Falkland Islands", featured: false },
  { code: "FO", name: "Faroe Islands", featured: false },
  { code: "FJ", name: "Fiji", featured: false },
  { code: "FI", name: "Finland", featured: false },
  { code: "FR", name: "France", featured: true },
  { code: "GF", name: "French Guiana", featured: false },
  { code: "PF", name: "French Polynesia", featured: false },
  { code: "TF", name: "French Southern Territories", featured: false },
  { code: "GA", name: "Gabon", featured: false },
  { code: "GM", name: "Gambia", featured: false },
  { code: "GE", name: "Georgia", featured: false },
  { code: "DE", name: "Germany", featured: true },
  { code: "GH", name: "Ghana", featured: true },
  { code: "GI", name: "Gibraltar", featured: false },
  { code: "GR", name: "Greece", featured: false },
  { code: "GL", name: "Greenland", featured: false },
  { code: "GD", name: "Grenada", featured: false },
  { code: "GP", name: "Guadeloupe", featured: false },
  { code: "GU", name: "Guam", featured: false },
  { code: "GT", name: "Guatemala", featured: false },
  { code: "GG", name: "Guernsey", featured: false },
  { code: "GN", name: "Guinea", featured: false },
  { code: "GW", name: "Guinea-Bissau", featured: false },
  { code: "GY", name: "Guyana", featured: false },
  { code: "HT", name: "Haiti", featured: true },
  { code: "HM", name: "Heard Island and McDonald Islands", featured: false },
  { code: "VA", name: "Holy See", featured: false },
  { code: "HN", name: "Honduras", featured: false },
  { code: "HK", name: "Hong Kong", featured: false },
  { code: "HU", name: "Hungary", featured: false },
  { code: "IS", name: "Iceland", featured: false },
  { code: "IN", name: "India", featured: false },
  { code: "ID", name: "Indonesia", featured: false },
  { code: "IR", name: "Iran", featured: true },
  { code: "IQ", name: "Iraq", featured: true },
  { code: "IE", name: "Ireland", featured: false },
  { code: "IM", name: "Isle of Man", featured: false },
  { code: "IL", name: "Israel", featured: false },
  { code: "IT", name: "Italy", featured: false },
  { code: "JM", name: "Jamaica", featured: false },
  { code: "JP", name: "Japan", featured: true },
  { code: "JE", name: "Jersey", featured: false },
  { code: "JO", name: "Jordan", featured: true },
  { code: "KZ", name: "Kazakhstan", featured: false },
  { code: "KE", name: "Kenya", featured: false },
  { code: "KI", name: "Kiribati", featured: false },
  { code: "KP", name: "Korea (North)", featured: false },
  { code: "KR", name: "Korea (South)", featured: true },
  { code: "KW", name: "Kuwait", featured: false },
  { code: "KG", name: "Kyrgyzstan", featured: false },
  { code: "LA", name: "Laos", featured: false },
  { code: "LV", name: "Latvia", featured: false },
  { code: "LB", name: "Lebanon", featured: false },
  { code: "LS", name: "Lesotho", featured: false },
  { code: "LR", name: "Liberia", featured: false },
  { code: "LY", name: "Libya", featured: false },
  { code: "LI", name: "Liechtenstein", featured: false },
  { code: "LT", name: "Lithuania", featured: false },
  { code: "LU", name: "Luxembourg", featured: false },
  { code: "MO", name: "Macao", featured: false },
  { code: "MG", name: "Madagascar", featured: false },
  { code: "MW", name: "Malawi", featured: false },
  { code: "MY", name: "Malaysia", featured: false },
  { code: "MV", name: "Maldives", featured: false },
  { code: "ML", name: "Mali", featured: false },
  { code: "MT", name: "Malta", featured: false },
  { code: "MH", name: "Marshall Islands", featured: false },
  { code: "MQ", name: "Martinique", featured: false },
  { code: "MR", name: "Mauritania", featured: false },
  { code: "MU", name: "Mauritius", featured: false },
  { code: "YT", name: "Mayotte", featured: false },
  { code: "MX", name: "Mexico", featured: true },
  { code: "FM", name: "Micronesia", featured: false },
  { code: "MD", name: "Moldova", featured: false },
  { code: "MC", name: "Monaco", featured: false },
  { code: "MN", name: "Mongolia", featured: false },
  { code: "ME", name: "Montenegro", featured: false },
  { code: "MS", name: "Montserrat", featured: false },
  { code: "MA", name: "Morocco", featured: true },
  { code: "MZ", name: "Mozambique", featured: false },
  { code: "MM", name: "Myanmar", featured: false },
  { code: "NA", name: "Namibia", featured: false },
  { code: "NR", name: "Nauru", featured: false },
  { code: "NP", name: "Nepal", featured: false },
  { code: "NL", name: "Netherlands", featured: true },
  { code: "NC", name: "New Caledonia", featured: false },
  { code: "NZ", name: "New Zealand", featured: true },
  { code: "NI", name: "Nicaragua", featured: false },
  { code: "NE", name: "Niger", featured: false },
  { code: "NG", name: "Nigeria", featured: false },
  { code: "NU", name: "Niue", featured: false },
  { code: "NF", name: "Norfolk Island", featured: false },
  { code: "MK", name: "North Macedonia", featured: false },
  { code: "MP", name: "Northern Mariana Islands", featured: false },
  { code: "NO", name: "Norway", featured: true },
  { code: "OM", name: "Oman", featured: false },
  { code: "PK", name: "Pakistan", featured: false },
  { code: "PW", name: "Palau", featured: false },
  { code: "PS", name: "Palestine", featured: false },
  { code: "PA", name: "Panama", featured: true },
  { code: "PG", name: "Papua New Guinea", featured: false },
  { code: "PY", name: "Paraguay", featured: true },
  { code: "PE", name: "Peru", featured: false },
  { code: "PH", name: "Philippines", featured: false },
  { code: "PN", name: "Pitcairn", featured: false },
  { code: "PL", name: "Poland", featured: false },
  { code: "PT", name: "Portugal", featured: true },
  { code: "PR", name: "Puerto Rico", featured: false },
  { code: "QA", name: "Qatar", featured: true },
  { code: "RE", name: "Réunion", featured: false },
  { code: "RO", name: "Romania", featured: false },
  { code: "RU", name: "Russia", featured: false },
  { code: "RW", name: "Rwanda", featured: false },
  { code: "BL", name: "Saint Barthélemy", featured: false },
  { code: "SH", name: "Saint Helena", featured: false },
  { code: "KN", name: "Saint Kitts and Nevis", featured: false },
  { code: "LC", name: "Saint Lucia", featured: false },
  { code: "MF", name: "Saint Martin (French part)", featured: false },
  { code: "PM", name: "Saint Pierre and Miquelon", featured: false },
  { code: "VC", name: "Saint Vincent and the Grenadines", featured: false },
  { code: "WS", name: "Samoa", featured: false },
  { code: "SM", name: "San Marino", featured: false },
  { code: "ST", name: "São Tomé and Príncipe", featured: false },
  { code: "SA", name: "Saudi Arabia", featured: true },
  { code: "SN", name: "Senegal", featured: true },
  { code: "RS", name: "Serbia", featured: false },
  { code: "SC", name: "Seychelles", featured: false },
  { code: "SL", name: "Sierra Leone", featured: false },
  { code: "SG", name: "Singapore", featured: false },
  { code: "SX", name: "Sint Maarten (Dutch part)", featured: false },
  { code: "SK", name: "Slovakia", featured: false },
  { code: "SI", name: "Slovenia", featured: false },
  { code: "SB", name: "Solomon Islands", featured: false },
  { code: "SO", name: "Somalia", featured: false },
  { code: "ZA", name: "South Africa", featured: true },
  { code: "GS", name: "South Georgia and the South Sandwich Islands", featured: false },
  { code: "SS", name: "South Sudan", featured: false },
  { code: "ES", name: "Spain", featured: true },
  { code: "LK", name: "Sri Lanka", featured: false },
  { code: "SD", name: "Sudan", featured: false },
  { code: "SR", name: "Suriname", featured: false },
  { code: "SJ", name: "Svalbard and Jan Mayen", featured: false },
  { code: "SE", name: "Sweden", featured: true },
  { code: "CH", name: "Switzerland", featured: true },
  { code: "SY", name: "Syria", featured: false },
  { code: "TW", name: "Taiwan", featured: false },
  { code: "TJ", name: "Tajikistan", featured: false },
  { code: "TZ", name: "Tanzania", featured: false },
  { code: "TH", name: "Thailand", featured: false },
  { code: "TL", name: "Timor-Leste", featured: false },
  { code: "TG", name: "Togo", featured: false },
  { code: "TK", name: "Tokelau", featured: false },
  { code: "TO", name: "Tonga", featured: false },
  { code: "TT", name: "Trinidad and Tobago", featured: false },
  { code: "TN", name: "Tunisia", featured: true },
  { code: "TR", name: "Türkiye", featured: true },
  { code: "TM", name: "Turkmenistan", featured: false },
  { code: "TC", name: "Turks and Caicos Islands", featured: false },
  { code: "TV", name: "Tuvalu", featured: false },
  { code: "UG", name: "Uganda", featured: false },
  { code: "UA", name: "Ukraine", featured: false },
  { code: "AE", name: "United Arab Emirates", featured: false },
  { code: "GB", name: "United Kingdom", featured: true },
  { code: "US", name: "United States", featured: true },
  { code: "UM", name: "United States Minor Outlying Islands", featured: false },
  { code: "UY", name: "Uruguay", featured: true },
  { code: "UZ", name: "Uzbekistan", featured: true },
  { code: "VU", name: "Vanuatu", featured: false },
  { code: "VE", name: "Venezuela", featured: false },
  { code: "VN", name: "Vietnam", featured: false },
  { code: "VG", name: "Virgin Islands (British)", featured: false },
  { code: "VI", name: "Virgin Islands (U.S.)", featured: false },
  { code: "WF", name: "Wallis and Futuna", featured: false },
  { code: "EH", name: "Western Sahara", featured: false },
  { code: "YE", name: "Yemen", featured: false },
  { code: "ZM", name: "Zambia", featured: false },
  { code: "ZW", name: "Zimbabwe", featured: false },
];

/**
 * The featured subset, in the same alphabetical order as COUNTRIES. Used by
 * the picker to render the "Featured" rail at the top.
 */
export const FEATURED_COUNTRIES: Country[] = COUNTRIES.filter((c) => c.featured);

/**
 * Cross-platform flag URL for `<img>` rendering. Backed by flagcdn.com
 * (free, CDN-cached, supports every ISO 3166-1 alpha-2 code).
 *
 * Returns null for "XX" / empty / non-2-letter codes; callers should
 * render a 🌐 fallback in that case.
 *
 * Width must be one of flagcdn's supported widths: 20, 40, 80, 160, 320,
 * 640, 1280, 2560. Pick the smallest that comfortably exceeds the
 * @2x-DPR pixel size you'll display at — the dropdown uses 20 (renders
 * at ~14×10 CSS px), the UserPill uses 40 (renders at ~20×15 CSS px).
 */
export function flagUrl(
  cc: string | null | undefined,
  width: 20 | 40 | 80 | 160 | 320 = 20,
): string | null {
  if (!cc || cc.length !== 2 || cc === UNSET_COUNTRY) return null;
  return `https://flagcdn.com/w${width}/${cc.toLowerCase()}.png`;
}

/**
 * Map alpha-2 → regional-indicator-pair emoji for flag rendering. NOTE:
 * does NOT render as a flag on Windows / most desktop Linux. Use
 * flagUrl() with `<img>` in UI; this helper exists for text contexts.
 * Returns 🌐 for "XX" / empty / non-2-letter codes.
 */
export function flag(cc: string | null | undefined): string {
  if (!cc || cc.length !== 2 || cc === UNSET_COUNTRY) return "🌐";
  const A = 0x1f1e6;
  return String.fromCodePoint(
    A + cc.toUpperCase().charCodeAt(0) - 65,
    A + cc.toUpperCase().charCodeAt(1) - 65,
  );
}

/** Lookup by code. Returns `null` if not in the table at all. */
export function findCountry(code: string): Country | null {
  const up = code.toUpperCase();
  return COUNTRIES.find((c) => c.code === up) ?? null;
}

/**
 * Find a Country by code, or return a synthesised `Country` for unknown
 * but valid 2-letter codes (so a server-set country we don't know about
 * still renders rather than showing nothing). Use for display fallback.
 */
export function resolveCountry(code: string | null | undefined): Country {
  if (!code || code.length !== 2 || code === UNSET_COUNTRY) {
    return { code: UNSET_COUNTRY, name: "Worldwide", featured: false };
  }
  const found = findCountry(code);
  if (found) return found;
  return { code: code.toUpperCase(), name: code.toUpperCase(), featured: false };
}
